import React, { Component } from 'react';
import { ExternalLink } from 'react-feather';
import { Alert, AlertButton, Button, Heading2, Heading3, StackView, Text } from 'localmed-core';

import { PanelRule } from '../../../legacyCore/LegacyPanel';
import { isUnavailable, isActive, needsInsuranceCollection } from '../../utils/appointmentUtils';
import {
  buildApptCalendarUrl,
  buildOfficeDirectionsUrl,
  buildProviderSchedulingUrl,
} from '../../utils/urls';
import AppointmentInsuranceForm from './AppointmentInsuranceForm';

import TrackClick from '../TrackClick';
import { TYPES } from '../../../widgets/components/AnnouncementBanner';

class AppointmentInstructions extends Component {
  static defaultProps = {
    justScheduled: false,
    justConfirmed: false,
    isWidget: false,
  };

  render() {
    const { justScheduled, justConfirmed, appointment, isWidget } = this.props;
    const { provider } = appointment;
    const { office, practice } = provider;
    const showInsuranceForm = needsInsuranceCollection(appointment);
    const justScheduledToWidget = isWidget || !provider.localmedDirectoryEnabled;
    const { announcementEnabled, announcement } = office;
    const covidProceduresLink =
      announcementEnabled && announcement.type === TYPES.COVID_19 && announcement.moreUrl;

    if (isUnavailable(appointment)) {
      return (
        <Alert tier="error">
          This appointment could not be scheduled because the time was not available.
        </Alert>
      );
    }

    if (!isActive(appointment)) {
      return (
        <StackView spacing="xxs">
          <Heading2 fontWeight="bold">Thanks for using LocalMed!</Heading2>
          <Text>
            Please let us know how your experience went by contacting us at{' '}
            <a href="mailto:support@localmed.com">support@localmed.com</a>.
          </Text>
        </StackView>
      );
    }

    return (
      <div>
        {justScheduled && (
          <React.Fragment>
            <Alert tier="success">
              <Heading2 color="inherit" marginBottom="xxs">
                Your appointment has been scheduled!
              </Heading2>
              <Text marginBottom="sm">
                Have more appointments to schedule? Let’s get those taken care of.
              </Text>
              <TrackClick
                event="Clicked Schedule Another Appointment"
                eventProperties={{ component: 'AppointmentInstructions' }}
              >
                {(getTrackingProps) => (
                  <AlertButton
                    data-qa="appointment-detail__schedule-another"
                    className="margin-right-sm"
                    {...{
                      [justScheduledToWidget ? 'to' : 'href']: buildProviderSchedulingUrl(
                        provider.id,
                        {
                          widget: justScheduledToWidget,
                        }
                      ),
                    }}
                    {...getTrackingProps()}
                  >
                    Schedule Another Appointment
                  </AlertButton>
                )}
              </TrackClick>
            </Alert>
            <PanelRule />
          </React.Fragment>
        )}

        {justConfirmed && (
          <React.Fragment>
            <Alert tier="success">
              <Heading2 color="inherit">Your appointment has been confirmed!</Heading2>
            </Alert>
            <PanelRule />
          </React.Fragment>
        )}

        {showInsuranceForm && (
          <React.Fragment>
            <AppointmentInsuranceForm appointment={appointment} />
            <PanelRule />
          </React.Fragment>
        )}

        {!showInsuranceForm && practice.intakeFormUrl && (
          <React.Fragment>
            <Heading3 fontWeight="bold" marginBottom="xxs">
              Want to speed things up?
            </Heading3>
            <Text marginBottom="sm">
              Filling out your check-in forms beforehand will reduce your wait time.
            </Text>
            <TrackClick
              event="Clicked Fill Out Check-in Forms"
              eventProperties={{ component: 'AppointmentInstructions' }}
            >
              {(getTrackingProps) => (
                <Button
                  tier="emphasis"
                  icon={<ExternalLink />}
                  href={practice.intakeFormUrl || ''}
                  target="_blank"
                  rel="noopener noreferrer"
                  {...getTrackingProps()}
                >
                  Fill Out Check-in Forms
                </Button>
              )}
            </TrackClick>
            <PanelRule />
          </React.Fragment>
        )}

        <Heading3 fontWeight="bold" marginBottom="xxs">
          Let’s get you ready for your appointment
        </Heading3>
        <Text marginBottom="sm">
          We want your appointment to be as smooth as possible, help us make that happen by
          completing a few things.
        </Text>

        <ul className="font-size-xs">
          {covidProceduresLink && (
            <li>
              <TrackClick
                event="Clicked Covid Procedures Link"
                eventProperties={{ component: 'AppointmentInstructions' }}
              >
                {(getTrackingProps) => (
                  <a
                    href={covidProceduresLink}
                    rel="noopener noreferrer"
                    target="_blank"
                    {...getTrackingProps()}
                  >
                    Read our COVID-19 procedures and guidelines
                  </a>
                )}
              </TrackClick>{' '}
            </li>
          )}
          <li>
            <TrackClick
              event="Clicked Add to Calendar"
              eventProperties={{ component: 'AppointmentInstructions' }}
            >
              {(getTrackingProps) => (
                <a
                  href={buildApptCalendarUrl(provider.id, appointment.id, {
                    patientToken: appointment.patientToken,
                  })}
                  {...getTrackingProps()}
                >
                  Add the appointment
                </a>
              )}
            </TrackClick>{' '}
            to your calendar.
          </li>
          <li>
            <TrackClick
              event="Clicked Get Directions"
              eventProperties={{ component: 'AppointmentInstructions' }}
            >
              {(getTrackingProps) => (
                <a
                  href={buildOfficeDirectionsUrl(office)}
                  target="_blank"
                  rel="noopener noreferrer"
                  {...getTrackingProps()}
                >
                  Get directions
                </a>
              )}
            </TrackClick>{' '}
            to the office.
          </li>
          <li>Check your email for appointment reminders.</li>
        </ul>

        <p>
          Call <strong>{office.phone}</strong> if you have any questions about your appointment.
        </p>
      </div>
    );
  }
}

export default AppointmentInstructions;
